import { Mail, MapPin, Phone, User } from "lucide-react";
import { useSiteSettings, useTeam } from "@/lib/cms";
import { useDocumentTitle } from "@/lib/use-document-title";
import { fileUrl } from "@/lib/format";
import { ScrollReveal } from "@/components/scroll-reveal";
import { DynamicForm } from "@/components/dynamic-form";

export function ContactPage() {
  const { brand, contact } = useSiteSettings();
  const teamQ = useTeam();
  useDocumentTitle(contact.pageTitle);

  return (
    <div>
      {/* Hero with watermark */}
      <section
        data-preview-section="contact-hero"
        className="relative bg-white py-24 lg:py-32 section-titled overflow-hidden"
      >
        <div className="section-watermark uppercase">kapcsolat</div>
        <div className="container relative z-10 max-w-3xl text-center">
          <ScrollReveal immediate>
            <span className="section-eyebrow">{contact.pageEyebrow}</span>
            <h1 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-heading font-normal uppercase tracking-ultrawide leading-tight">
              <span className="text-brand-700">
                {contact.pageTitle.split(" ").slice(0, -1).join(" ")}
              </span>{" "}
              <span className="text-accent">
                {contact.pageTitle.split(" ").slice(-1).join(" ")}
              </span>
            </h1>
            <p className="mt-6 text-brand-500 leading-relaxed max-w-xl mx-auto">
              {contact.pageSubtitle}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Address strip */}
      <section className="py-12 border-y border-brand-100 bg-brand-50">
        <div className="container grid sm:grid-cols-3 gap-6 text-center sm:text-left max-w-5xl">
          {[
            { icon: MapPin, label: "Cím", value: brand.address, href: undefined },
            {
              icon: Phone,
              label: "Telefon",
              value: brand.phone,
              href: `tel:${brand.phone.replace(/\s/g, "")}`,
            },
            {
              icon: Mail,
              label: "Email",
              value: brand.email,
              href: `mailto:${brand.email}`,
            },
          ].map(({ icon: Icon, label, value, href }, i) => (
            <ScrollReveal
              key={label}
              delay={(i % 3) as 0 | 1 | 2}
              className="flex items-start sm:items-center gap-4 justify-center sm:justify-start"
            >
              <div className="w-11 h-11 bg-white text-accent grid place-items-center shrink-0 border border-brand-200">
                <Icon size={18} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widish text-brand-400 font-semibold">
                  {label}
                </div>
                {href ? (
                  <a
                    href={href}
                    className="text-sm text-brand-700 hover:text-accent mt-1 inline-block"
                  >
                    {value}
                  </a>
                ) : (
                  <div className="text-sm text-brand-700 mt-1">{value}</div>
                )}
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Team */}
      {teamQ.data && teamQ.data.length > 0 && (
        <section className="relative py-20 lg:py-28 bg-white section-titled overflow-hidden">
          <div className="section-watermark uppercase">csapat</div>
          <div className="container relative z-10">
            <ScrollReveal className="text-center mb-14">
              <span className="section-eyebrow">{contact.teamSectionEyebrow}</span>
              <h2 className="mt-3 text-2xl sm:text-3xl font-heading font-normal uppercase tracking-ultrawide text-brand-700">
                {contact.teamSectionTitle}
              </h2>
            </ScrollReveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {teamQ.data.map((m, i) => (
                <ScrollReveal
                  key={m.id}
                  delay={(i % 3) as 0 | 1 | 2}
                  className="group bg-white border border-brand-200 hover:border-accent transition-all duration-300 p-6"
                >
                  {m.photoR2Key ? (
                    <img
                      src={fileUrl(m.photoR2Key)}
                      alt={m.name}
                      className="w-20 h-20 rounded-full object-cover mb-5"
                    />
                  ) : (
                    <div className="w-14 h-14 bg-brand-100 text-brand-400 group-hover:bg-accent group-hover:text-white transition-colors grid place-items-center mb-5">
                      <User size={24} strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="font-semibold uppercase tracking-widish text-brand-700">
                    {m.name}
                  </div>
                  <div className="text-xs text-accent mt-1 uppercase tracking-widish">
                    {m.role}
                  </div>
                  <div className="mt-5 space-y-2 text-sm">
                    {m.email && (
                      <a
                        href={`mailto:${m.email}`}
                        className="flex items-center gap-2 text-brand-500 hover:text-brand-700"
                      >
                        <Mail size={13} className="text-brand-400" />
                        {m.email}
                      </a>
                    )}
                    {m.phone && (
                      <a
                        href={`tel:${m.phone.replace(/\s/g, "")}`}
                        className="flex items-center gap-2 text-brand-500 hover:text-brand-700"
                      >
                        <Phone size={13} className="text-brand-400" />
                        {m.phone}
                      </a>
                    )}
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Form */}
      <section className="relative py-20 lg:py-28 bg-brand-50 section-titled overflow-hidden">
        <div className="section-watermark uppercase">üzenet</div>
        <div className="container relative z-10 max-w-2xl">
          <ScrollReveal className="text-center mb-10">
            <span className="section-eyebrow">{contact.formSectionEyebrow}</span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-heading font-normal uppercase tracking-ultrawide text-brand-700">
              {contact.formSectionTitle}
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={1} className="bg-white p-8 lg:p-10 border border-brand-100">
            <DynamicForm />
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
